package com.mgacreative.hotelru.feature.finance.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun ProfitReportScreen(
    viewModel: FinanceViewModel = koinViewModel(),
    onNavigateBack: () -> Unit = {},
    onNavigate: (String) -> Unit = {}
) {
    val movements by viewModel.movements.collectAsState()
    var selectedPeriod by remember { mutableStateOf("ALL") } // THIS_MONTH, LAST_3_MONTHS, THIS_YEAR, ALL
    var showExportSnackbar by remember { mutableStateOf(false) }

    val incomeMovements = movements.filter { it.type == "INCOME" }
    val expenseMovements = movements.filter { it.type == "EXPENSE" }

    val totalIncome = incomeMovements.sumOf { it.amount }
    val totalExpense = expenseMovements.sumOf { it.amount }
    val netProfit = totalIncome - totalExpense
    val profitMargin = if (totalIncome > 0) ((netProfit / totalIncome) * 100).coerceIn(-100.0, 100.0) else 0.0

    // Group expenses by category prefix (before the dash)
    val expenseCategories = expenseMovements
        .groupBy { tx ->
            val desc = tx.description ?: "Diğer Gider"
            if (desc.contains("-")) desc.substringBefore("-").trim() else desc
        }
        .mapValues { entry -> entry.value.sumOf { it.amount } }
        .toList()
        .sortedByDescending { it.second }

    // Group income by category prefix
    val incomeCategories = incomeMovements
        .groupBy { tx ->
            val desc = tx.description ?: "Diğer Gelir"
            if (desc.contains("-")) desc.substringBefore("-").trim() else desc
        }
        .mapValues { entry -> entry.value.sumOf { it.amount } }
        .toList()
        .sortedByDescending { it.second }

    val snackbarHostState = remember { SnackbarHostState() }

    val exportMsg = l10n("Finansal rapor PDF formatında başarıyla hazırlandı.")
    LaunchedEffect(showExportSnackbar) {
        if (showExportSnackbar) {
            snackbarHostState.showSnackbar(exportMsg)
            showExportSnackbar = false
        }
    }

    val listState = rememberLazyListState()

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(
                            onClick = onNavigateBack,
                            modifier = Modifier
                                .size(36.dp)
                                .background(Color(0xFFF1F5F9), CircleShape)
                        ) {
                            Icon(
                                Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = l10n("Back"),
                                tint = Color(0xFF1E293B),
                                modifier = Modifier.size(18.dp)
                            )
                        }
                        Spacer(modifier = Modifier.width(12.dp))
                        Column {
                            Text(
                                text = l10n("Kâr / Zarar & Finansal Rapor"),
                                fontWeight = FontWeight.Bold,
                                fontSize = 18.sp,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = l10n("Otel Gelir, Gider ve Net Kârlılık Analizi"),
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    Button(
                        onClick = { showExportSnackbar = true },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0F172A)),
                        shape = RoundedCornerShape(10.dp),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp)
                    ) {
                        Icon(Icons.Default.DateRange, contentDescription = null, tint = Color.White, modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(6.dp))
                        Text(l10n("Raporu İndir"), fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                    }
                }
            }
        },
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        bottomBar = {
            FinanceBottomNavBar(
                selectedTab = 2,
                onNavigate = onNavigate
            )
        }
    ) { paddingValues ->
        LazyColumn(
            state = listState,
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF8FAFC))
                .verticalScrollbar(listState),
            contentPadding = PaddingValues(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // Period Selector Chips
            item {
                Surface(
                    shape = RoundedCornerShape(14.dp),
                    color = Color.White,
                    border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(8.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        PeriodTabButton(
                            title = l10n("Bu Ay"),
                            isSelected = selectedPeriod == "THIS_MONTH",
                            modifier = Modifier.weight(1f),
                            onClick = { selectedPeriod = "THIS_MONTH" }
                        )
                        PeriodTabButton(
                            title = l10n("Son 3 Ay"),
                            isSelected = selectedPeriod == "LAST_3_MONTHS",
                            modifier = Modifier.weight(1f),
                            onClick = { selectedPeriod = "LAST_3_MONTHS" }
                        )
                        PeriodTabButton(
                            title = l10n("Bu Yıl"),
                            isSelected = selectedPeriod == "THIS_YEAR",
                            modifier = Modifier.weight(1f),
                            onClick = { selectedPeriod = "THIS_YEAR" }
                        )
                        PeriodTabButton(
                            title = l10n("Tüm Zamanlar"),
                            isSelected = selectedPeriod == "ALL",
                            modifier = Modifier.weight(1f),
                            onClick = { selectedPeriod = "ALL" }
                        )
                    }
                }
            }

            // Main Net Profit & Margin Card
            item {
                Surface(
                    shape = RoundedCornerShape(18.dp),
                    color = Color.White,
                    border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    shadowElevation = 2.dp,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(20.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Column {
                                Text(
                                    text = l10n("NET DÖNEM KÂRI / ZARARI"),
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFF64748B),
                                    letterSpacing = 0.5.sp
                                )
                                Spacer(modifier = Modifier.height(4.dp))
                                Text(
                                    text = "₽ ${netProfit.format()}",
                                    fontWeight = FontWeight.ExtraBold,
                                    fontSize = 28.sp,
                                    color = if (netProfit >= 0) Color(0xFF16A34A) else Color(0xFFDC2626)
                                )
                            }

                            // Kâr Marjı Badge
                            Surface(
                                shape = RoundedCornerShape(12.dp),
                                color = if (netProfit >= 0) Color(0xFFDCFCE7) else Color(0xFFFEE2E2),
                                border = androidx.compose.foundation.BorderStroke(
                                    1.dp,
                                    if (netProfit >= 0) Color(0xFF86EFAC) else Color(0xFFFCA5A5)
                                )
                            ) {
                                Column(
                                    modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(
                                        text = l10n("Kâr Marjı"),
                                        fontSize = 11.sp,
                                        fontWeight = FontWeight.Medium,
                                        color = if (netProfit >= 0) Color(0xFF166534) else Color(0xFF991B1B)
                                    )
                                    Text(
                                        text = "% ${profitMargin.format()}",
                                        fontSize = 16.sp,
                                        fontWeight = FontWeight.ExtraBold,
                                        color = if (netProfit >= 0) Color(0xFF166534) else Color(0xFF991B1B)
                                    )
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(18.dp))

                        // Progress Ratio Bar
                        val incomeFraction = if (totalIncome + totalExpense > 0) (totalIncome / (totalIncome + totalExpense)).toFloat() else 0.5f
                        Column {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Text(
                                    text = "${l10n("Gelir Oranı")}: %${(incomeFraction * 100).toInt()}",
                                    fontSize = 12.sp,
                                    color = Color(0xFF16A34A),
                                    fontWeight = FontWeight.Bold
                                )
                                Text(
                                    text = "${l10n("Gider Oranı")}: %${((1f - incomeFraction) * 100).toInt()}",
                                    fontSize = 12.sp,
                                    color = Color(0xFFDC2626),
                                    fontWeight = FontWeight.Bold
                                )
                            }
                            Spacer(modifier = Modifier.height(6.dp))
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(10.dp)
                                    .clip(RoundedCornerShape(5.dp))
                                    .background(Color(0xFFFEE2E2))
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth(incomeFraction)
                                        .fillMaxHeight()
                                        .background(Color(0xFF16A34A))
                                )
                            }
                        }
                    }
                }
            }

            // Income & Expense KPI Duo
            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    // Toplam Gelir
                    Surface(
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(16.dp),
                        color = Color.White,
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        shadowElevation = 1.dp
                    ) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(l10n("Toplam Gelir"), color = Color(0xFF64748B), fontSize = 13.sp, fontWeight = FontWeight.Medium)
                                Box(
                                    modifier = Modifier.size(28.dp).background(Color(0xFFDCFCE7), CircleShape),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Icon(Icons.Default.KeyboardArrowUp, contentDescription = null, tint = Color(0xFF16A34A), modifier = Modifier.size(18.dp))
                                }
                            }
                            Spacer(modifier = Modifier.height(6.dp))
                            Text("₽ ${totalIncome.format()}", fontWeight = FontWeight.Bold, fontSize = 20.sp, color = Color(0xFF16A34A))
                            Spacer(modifier = Modifier.height(4.dp))
                            Text("${incomeMovements.size} ${l10n("Gelir Kaydı")}", fontSize = 11.sp, color = Color(0xFF94A3B8))
                        }
                    }

                    // Toplam Gider
                    Surface(
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(16.dp),
                        color = Color.White,
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        shadowElevation = 1.dp
                    ) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(l10n("Toplam Gider"), color = Color(0xFF64748B), fontSize = 13.sp, fontWeight = FontWeight.Medium)
                                Box(
                                    modifier = Modifier.size(28.dp).background(Color(0xFFFEE2E2), CircleShape),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Icon(Icons.Default.KeyboardArrowDown, contentDescription = null, tint = Color(0xFFDC2626), modifier = Modifier.size(18.dp))
                                }
                            }
                            Spacer(modifier = Modifier.height(6.dp))
                            Text("₽ ${totalExpense.format()}", fontWeight = FontWeight.Bold, fontSize = 20.sp, color = Color(0xFFDC2626))
                            Spacer(modifier = Modifier.height(4.dp))
                            Text("${expenseMovements.size} ${l10n("Gider Kaydı")}", fontSize = 11.sp, color = Color(0xFF94A3B8))
                        }
                    }
                }
            }

            // Gider Dağılımı Bölümü
            item {
                Surface(
                    shape = RoundedCornerShape(16.dp),
                    color = Color.White,
                    border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(20.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = l10n("Gider Dağılımı (Kategori Bazlı)"),
                                fontWeight = FontWeight.Bold,
                                fontSize = 15.sp,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = "₽ ${totalExpense.format()}",
                                fontWeight = FontWeight.Bold,
                                fontSize = 13.sp,
                                color = Color(0xFFDC2626)
                            )
                        }

                        Spacer(modifier = Modifier.height(16.dp))

                        if (expenseCategories.isEmpty()) {
                            Text(l10n("Kayıtlı gider bulunmamaktadır."), fontSize = 13.sp, color = Color(0xFF94A3B8))
                        } else {
                            expenseCategories.forEach { (catName, catAmount) ->
                                val catFraction = if (totalExpense > 0) (catAmount / totalExpense).toFloat() else 0f
                                Column(modifier = Modifier.padding(vertical = 6.dp)) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Text(
                                            text = l10n(catName),
                                            fontSize = 13.sp,
                                            fontWeight = FontWeight.Medium,
                                            color = Color(0xFF334155)
                                        )
                                        Text(
                                            text = "₽ ${catAmount.format()} (%${(catFraction * 100).toInt()})",
                                            fontSize = 13.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = Color(0xFF0F172A)
                                        )
                                    }
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(6.dp)
                                            .clip(RoundedCornerShape(3.dp))
                                            .background(Color(0xFFF1F5F9))
                                    ) {
                                        Box(
                                            modifier = Modifier
                                                .fillMaxWidth(catFraction)
                                                .fillMaxHeight()
                                                .background(Color(0xFFDC2626))
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Gelir Dağılımı Bölümü
            item {
                Surface(
                    shape = RoundedCornerShape(16.dp),
                    color = Color.White,
                    border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(20.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = l10n("Gelir Kaynakları Dağılımı"),
                                fontWeight = FontWeight.Bold,
                                fontSize = 15.sp,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = "₽ ${totalIncome.format()}",
                                fontWeight = FontWeight.Bold,
                                fontSize = 13.sp,
                                color = Color(0xFF16A34A)
                            )
                        }

                        Spacer(modifier = Modifier.height(16.dp))

                        if (incomeCategories.isEmpty()) {
                            Text(l10n("Kayıtlı gelir bulunmamaktadır."), fontSize = 13.sp, color = Color(0xFF94A3B8))
                        } else {
                            incomeCategories.forEach { (catName, catAmount) ->
                                val catFraction = if (totalIncome > 0) (catAmount / totalIncome).toFloat() else 0f
                                Column(modifier = Modifier.padding(vertical = 6.dp)) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Text(
                                            text = l10n(catName),
                                            fontSize = 13.sp,
                                            fontWeight = FontWeight.Medium,
                                            color = Color(0xFF334155)
                                        )
                                        Text(
                                            text = "₽ ${catAmount.format()} (%${(catFraction * 100).toInt()})",
                                            fontSize = 13.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = Color(0xFF0F172A)
                                        )
                                    }
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(6.dp)
                                            .clip(RoundedCornerShape(3.dp))
                                            .background(Color(0xFFF1F5F9))
                                    ) {
                                        Box(
                                            modifier = Modifier
                                                .fillMaxWidth(catFraction)
                                                .fillMaxHeight()
                                                .background(Color(0xFF16A34A))
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            item {
                Spacer(modifier = Modifier.height(40.dp))
            }
        }
    }
}

@Composable
fun PeriodTabButton(
    title: String,
    isSelected: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    Surface(
        modifier = modifier.clickable(onClick = onClick),
        shape = RoundedCornerShape(10.dp),
        color = if (isSelected) Color(0xFF2563EB) else Color.Transparent
    ) {
        Box(
            modifier = Modifier.padding(vertical = 8.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = title,
                fontSize = 12.sp,
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                color = if (isSelected) Color.White else Color(0xFF64748B)
            )
        }
    }
}
